// =================================================================
// Engine: Safety gating module (PSD §4.2.14 / TODO Task 1.6)
// =================================================================
import { Indication, PopulationMode, RenalStability, Strategy, ToxicityRisk, } from '@amino-pk/shared-types';
import { checkImplausibleInputs } from './validation';
/**
 * Extended-interval contraindication screening (FR-11.1 / PSD §4.2.14a).
 */
export function checkContraindications(patient, strategy) {
    const mode = strategy.populationMode;
    const checks = [
        { type: 'pregnancy', active: mode === PopulationMode.Pregnancy, severity: 'block', reason: 'Pregnancy — extended-interval contraindicated.' },
        { type: 'burns', active: mode === PopulationMode.BurnsAscites, severity: 'block', reason: 'Burns ≥20% BSA — altered PK, EI contraindicated.' },
        { type: 'ascites', active: mode === PopulationMode.BurnsAscites, severity: 'block', reason: 'Ascites — altered Vd, EI contraindicated.' },
        {
            type: 'dialysis',
            active: [PopulationMode.Hemodialysis, PopulationMode.CVVHD_CRRT, PopulationMode.PeritonealDialysis].includes(mode),
            severity: 'block',
            reason: 'Dialysis — requires dialysis-specific dosing pathway.',
        },
        { type: 'pediatric', active: patient.ageYears < 18, severity: 'block', reason: 'Age < 18 — pediatric pathway required (Phase 3).' },
        { type: 'cystic-fibrosis', active: mode === PopulationMode.CysticFibrosis, severity: 'block', reason: 'Cystic fibrosis — altered PK, use CF pathway.' },
        { type: 'endocarditis', active: patient.indication === Indication.Endocarditis, severity: 'warn', reason: 'Endocarditis synergy — traditional divided dosing preferred.' },
        { type: 'meningitis', active: patient.indication === Indication.Meningitis, severity: 'warn', reason: 'Meningitis — consider alternative dosing.' },
    ];
    return checks.filter((c) => c.active).map((c) => ({
        type: c.type,
        severity: c.severity,
        overridden: false,
        overrideReason: undefined,
    }));
}
/** Aggregate nephro/ototoxicity risk (FR-11.6 / PSD §4.2.14f). */
export function aggregateToxicityRisk(durationDays, cumulativeDoseMg, nephrotoxinCount, supratherapeuticTroughs) {
    const factors = [];
    let score = 0;
    if (durationDays > 7) {
        score += 2;
        factors.push(`Therapy duration ${durationDays} days (>7d)`);
    }
    else if (durationDays > 3) {
        score += 1;
        factors.push(`Therapy duration ${durationDays} days`);
    }
    if (cumulativeDoseMg > 0 && cumulativeDoseMg / 70 > 3000) {
        score += 1;
        factors.push('High cumulative dose');
    }
    if (nephrotoxinCount >= 2) {
        score += 2;
        factors.push(`${nephrotoxinCount} concurrent nephrotoxins`);
    }
    else if (nephrotoxinCount === 1) {
        score += 1;
        factors.push('1 concurrent nephrotoxin');
    }
    if (supratherapeuticTroughs >= 2) {
        score += 2;
        factors.push(`${supratherapeuticTroughs} supratherapeutic troughs`);
    }
    else if (supratherapeuticTroughs === 1) {
        score += 1;
        factors.push('1 supratherapeutic trough');
    }
    let risk;
    if (score >= 4)
        risk = ToxicityRisk.High;
    else if (score >= 2)
        risk = ToxicityRisk.Moderate;
    else
        risk = ToxicityRisk.Low;
    return { risk, factors };
}
/**
 * Full safety assessment (PSD §4.2.14). Orchestrates all gating.
 */
export function assessSafety(patient, _dosingWeight, renal, strategy, protocol, overrides = {}, durationDays = 1, cumulativeDoseMg = 0, supratherapeuticTroughs = 0) {
    const isEI = strategy.strategy === Strategy.ExtendedInterval;
    const baseContras = checkContraindications(patient, strategy);
    const contraindications = baseContras.map((c) => overrides[c.type]
        ? { ...c, overridden: true, overrideReason: overrides[c.type] }
        : c);
    const blockContras = contraindications.filter((c) => c.severity === 'block' && !c.overridden);
    const eiBlocked = isEI && blockContras.length > 0;
    const eiBlockReasons = blockContras.map((c) => `contraindication: ${c.type}`);
    const renalGatePassed = renal.crClMlMin >= protocol.renalCutoffMlMin;
    if (isEI && !renalGatePassed) {
        eiBlockReasons.push(`CrCl ${renal.crClMlMin.toFixed(1)} below threshold ${protocol.renalCutoffMlMin}`);
    }
    const implausibleInput = checkImplausibleInputs(patient).map((f) => ({
        field: f.field,
        value: f.value,
        reason: f.reason,
        validRange: f.validRange,
    }));
    const unstableRenalWarning = renal.stability === RenalStability.Unstable;
    const outOfModel = strategy.populationMode !== PopulationMode.Standard &&
        strategy.populationMode !== PopulationMode.Obesity;
    const nonOverridableCaution = outOfModel && blockContras.length === 0;
    const nephrotoxinCount = patient.concurrentNephrotoxins?.filter((n) => n.active).length ?? 0;
    const { risk, factors } = aggregateToxicityRisk(durationDays, cumulativeDoseMg, nephrotoxinCount, supratherapeuticTroughs);
    return {
        contraindications,
        eiBlocked: eiBlocked || (isEI && !renalGatePassed),
        eiBlockReasons,
        renalGatePassed,
        unstableRenalWarning,
        implausibleInput,
        nonOverridableCaution,
        cautionReason: nonOverridableCaution
            ? 'Patient in a special-population pathway — use clinical judgment.'
            : undefined,
        toxicityRisk: risk,
        toxicityRiskFactors: factors,
    };
}
//# sourceMappingURL=safety.js.map